import {
    Box,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    ListItemDecorator,
    tabClasses,
    useColorScheme,
} from "@mui/joy";
import { Package, Wrench, Calculator, CircleDollarSign } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import Materials from "./Materials";
import Labor from "./Labor";
import Estimating from "./Estimating";
import FinanceBreakdown from "./FinanceBreakdown";
import type { MaterialRowItem } from "../../utils/materialLineItems";
import type { LaborRowItem } from "../../types";

/** Below app header (48px) + sticky task progress stepper (~70px). */
const STICKY_TAB_TOP = "118px";

const tabListSx = {
    gap: 1,
    width: "calc(100% + 48px)",
    mx: -3,
    px: 2.5,
    position: "sticky",
    top: STICKY_TAB_TOP,
    zIndex: 1001,
    backgroundColor: "var(--standard-palette-background-surface)",
    borderColor: "divider",
    borderBottom: "1px solid",
    borderBottomColor: "divider",
    [`&& .${tabClasses.root}`]: {
        flex: "initial",
        color: "text.secondary",
        opacity: 0.7,
        background: "transparent",
        bgcolor: "transparent",
        paddingLeft: 0.5,
        paddingRight: 0.5,
        paddingBottom: 1,
        paddingTop: 1,
        fontSize: "sm",
        minHeight: 36,
        fontWeight: 600,
        "&:hover": {
            bgcolor: "transparent",
        },
        [`&.${tabClasses.selected}`]: {
            color: "primary.plainColor",
            fontWeight: 600,
            opacity: 1,
            "&::after": {
                height: 2,
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3,
                bgcolor: "primary.500",
            },
        },
    },
};

export function LineItems(props: {
    projectId?: string;
    materialsByGroup: Record<string, MaterialRowItem[]>;
    materialsLoading?: boolean;
    materialsError?: string | null;
    laborRows: LaborRowItem[];
    laborLoading?: boolean;
    laborError?: string | null;
}) {
    const {
        projectId,
        materialsByGroup,
        materialsLoading,
        materialsError,
        laborRows,
        laborLoading,
        laborError,
    } = props;
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get("taskTab") || "materials";
    const { mode } = useColorScheme();

    return (
        <Box mt={2}>
            <Tabs
                value={activeTab}
                sx={{ width: "100%" }}
                onChange={(_, value) => {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set("taskTab", value as string);
                        return params;
                    });
                }}
            >
                <TabList
                    sx={{
                        ...tabListSx,
                        bgcolor: "background.surface",
                    }}
                >
                    <Tab value="materials">
                        <ListItemDecorator>
                            <Package size={16} />
                        </ListItemDecorator>
                        Materials
                    </Tab>
                    <Tab value="labor">
                        <ListItemDecorator>
                            <Wrench size={16} />
                        </ListItemDecorator>
                        Labor
                    </Tab>
                    <Tab value="estimating">
                        <ListItemDecorator>
                            <Calculator size={16} />
                        </ListItemDecorator>
                        Estimating
                    </Tab>
                    <Tab value="summary">
                        <ListItemDecorator>
                            <CircleDollarSign size={16} />
                        </ListItemDecorator>
                        Summary
                    </Tab>
                </TabList>

                <TabPanel value="materials" keepMounted sx={{ px: 0, pt: 2 }}>
                    <Materials
                        projectId={projectId}
                        materialsByGroup={materialsByGroup}
                        loading={materialsLoading}
                        error={materialsError}
                    />
                </TabPanel>

                <TabPanel value="labor" keepMounted sx={{ px: 0, pt: 2 }}>
                    <Labor
                        projectId={projectId}
                        laborRows={laborRows}
                        loading={laborLoading}
                        error={laborError}
                        materialsByGroup={materialsByGroup}
                    />
                </TabPanel>

                <TabPanel value="estimating" keepMounted sx={{ px: 0, pt: 2 }}>
                    <Estimating projectId={projectId} />
                </TabPanel>

                <TabPanel value="summary" keepMounted sx={{ px: 0, pt: 2 }}>
                    <FinanceBreakdown projectId={projectId} />
                </TabPanel>
            </Tabs>
        </Box>
    );
}
